import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLIENT_DECISION_LABELS,
  DECISION_STATUS_LABELS,
  EFFECT_SOURCES,
  EFFECT_SOURCE_LABELS,
  ENTITY_TYPE_LABELS,
  EXECUTORS,
  EXECUTOR_LABELS,
  EXPERIMENT_OUTCOMES,
  EXPERIMENT_OUTCOME_LABELS,
  INITIATIVE_STATUS_LABELS,
  LEARNING_SCOPES,
  LEARNING_SCOPE_LABELS,
  MEASUREMENT_KINDS,
  MEASUREMENT_KIND_LABELS,
  type EffectSource,
  type Executor,
  type ExperimentOutcome,
  type InitiativeKind,
  type LearningScope,
  type MeasurementKind,
} from '@raro/domain';
import { Button, cx, EmptyState, Field, Icon, Input, Label, LineChart, Notice, Select, StatBig, Tag, Textarea, Thumb } from '@raro/ui';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDate, formatMoney } from '../lib/format';
import type { Decision, Effect, EntitySummary, Hypothesis, Initiative, Learning, Solution } from '../lib/types';
import { EntityPicker } from './EntityPicker';
import { ImagePicker } from './ImagePicker';
import { InitiativeBudget } from './InitiativeBudget';
import { MachineFill } from './MachineFill';
import { SeriesLink } from './SeriesLink';

export function useVerInvalidate(projectId: string) {
  const qc = useQueryClient();
  return () => {
    for (const k of ['initiatives', 'decisions', 'effects', 'learnings', 'ver', 'summary', 'entities', 'hypothesis', 'hypotheses', 'solution', 'sessions', 'judgments']) void qc.invalidateQueries({ queryKey: [k, projectId] });
    void qc.invalidateQueries({ queryKey: ['hypothesis'] });
    void qc.invalidateQueries({ queryKey: ['solution'] });
    void qc.invalidateQueries({ queryKey: ['ver-overview'] });
  };
}

export function Opener({ open, onOpen, label, children }: { open: boolean; onOpen: () => void; label: string; children: ReactNode }) {
  if (!open) return <Button size="sm" variant="ghost" onClick={onOpen} style={{ alignSelf: 'flex-start' }}>{label}</Button>;
  return <div className="rr-card rr-stack rr-stack--4">{children}</div>;
}

const inWeeks = (n: number) => new Date(Date.now() + n * 7 * 86400000).toISOString().slice(0, 10);

// ───────────────────────── INICIATIVAS (fazer + provar) ─────────────────────────

type InitiativeForm = {
  kind: InitiativeKind;
  title: string;
  description: string;
  origin: string; // `claim:<id>` | `hypothesis:<id>` | `decision:<id>`
  executor: Executor;
  ownerName: string;
  expectedEffect: string;
  measurementKind: MeasurementKind;
  measurementHow: string;
  measurementDate: string;
  weakeningResult: string;
  estimatedHours: string;
};
const emptyInitiative = (kind: InitiativeKind = 'initiative'): InitiativeForm => ({ kind, title: '', description: '', origin: '', executor: 'raro', ownerName: '', expectedEffect: '', measurementKind: kind === 'experiment' ? 'signal' : 'quantitative', measurementHow: '', measurementDate: inWeeks(kind === 'experiment' ? 3 : 8), weakeningResult: '', estimatedHours: '' });

/**
 * FAZER/PROVAR: iniciativas e experimentos. Toda iniciativa nasce de uma afirmação da Solução,
 * de uma decisão ou de uma hipótese, e já diz qual efeito espera, como e quando vai aferir.
 */
export function InitiativesSection({ projectId, canEdit, preset }: { projectId: string; canEdit: boolean; preset?: Partial<InitiativeForm> }) {
  const invalidate = useVerInvalidate(projectId);
  const list = useQuery({ queryKey: ['initiatives', projectId], queryFn: () => api.get<Initiative[]>(`/projects/${projectId}/initiatives`) });
  const hyps = useQuery({ queryKey: ['hypotheses', projectId], queryFn: () => api.get<Hypothesis[]>(`/projects/${projectId}/hypotheses`) });
  const decisions = useQuery({ queryKey: ['decisions', projectId], queryFn: () => api.get<Decision[]>(`/projects/${projectId}/decisions`) });
  const solution = useQuery({ queryKey: ['solution', projectId], queryFn: () => api.get<Solution | null>(`/projects/${projectId}/solution`) });
  const [open, setOpen] = useState(Boolean(preset));
  const [f, setF] = useState<InitiativeForm>({ ...emptyInitiative(preset?.kind), ...preset });
  const [filter, setFilter] = useState<'all' | 'initiative' | 'experiment' | 'overdue'>('all');
  const create = useMutation({
    mutationFn: () => {
      const [originType, originId] = f.origin.split(':');
      return api.post(`/projects/${projectId}/initiatives`, {
        kind: f.kind,
        title: f.title,
        description: f.description || undefined,
        claimId: originType === 'claim' ? originId : undefined,
        hypothesisId: originType === 'hypothesis' ? originId : undefined,
        decisionId: originType === 'decision' ? originId : undefined,
        executor: f.executor,
        ownerName: f.ownerName || undefined,
        expectedEffect: f.expectedEffect,
        measurementKind: f.measurementKind,
        measurementHow: f.measurementHow,
        measurementDate: new Date(`${f.measurementDate}T12:00:00`).toISOString(),
        weakeningResult: f.kind === 'experiment' ? f.weakeningResult : undefined,
        estimatedHours: f.estimatedHours ? Number(f.estimatedHours) : undefined,
      });
    },
    onSuccess: () => { setOpen(false); setF(emptyInitiative()); invalidate(); },
  });
  const acceptedClaims = solution.data?.claims.filter((c) => c.humanStatus === 'accepted') ?? [];
  const ok = f.title.trim().length > 1 && f.origin && f.expectedEffect.trim().length > 2 && f.measurementHow.trim().length > 2 && f.measurementDate && (f.kind !== 'experiment' || f.weakeningResult.trim().length > 2);
  const rows = (list.data ?? []).filter((i) => (filter === 'all' ? true : filter === 'overdue' ? i.measurementOverdue : i.kind === filter));
  const overdue = list.data?.filter((i) => i.measurementOverdue).length ?? 0;
  const live = (list.data ?? []).filter((i) => i.status !== 'dropped');
  const totals = { estimated: live.reduce((a, i) => a + (i.budget?.estimated ?? 0), 0), actual: live.reduce((a, i) => a + (i.budget?.actual ?? 0), 0), withBudget: live.filter((i) => (i.budget?.items ?? 0) > 0).length };
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Iniciativas</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>o que a Solução vira na prática. toda iniciativa já nasce dizendo qual efeito espera e quando vai aferir.</span>
      </div>
      {totals.withBudget > 0 && (
        <div className="rr-results-grid">
          <StatBig value={String(live.length)} label="iniciativas ativas" />
          <StatBig value={formatMoney(totals.estimated)} label={`estimativa de produção · ${totals.withBudget} com orçamento`} />
          <StatBig value={formatMoney(totals.actual)} label="realizado até agora" tone={totals.actual > totals.estimated && totals.estimated > 0 ? 'down' : undefined} />
        </div>
      )}
      <div className="rr-row rr-row--between rr-row--wrap">
        <div className="rr-row">
          {(['all', 'initiative', 'experiment', 'overdue'] as const).map((k) => (
            <button key={k} type="button" className="rr-label" style={{ color: filter === k ? 'var(--rr-text)' : undefined }} onClick={() => setFilter(k)}>
              {k === 'all' ? 'todas' : k === 'initiative' ? 'iniciativas' : k === 'experiment' ? 'experimentos' : `aferição vencida${overdue ? ` (${overdue})` : ''}`}
            </button>
          ))}
        </div>
        {canEdit && !open && (
          <div className="rr-row">
            <Button size="sm" variant="quiet" onClick={() => { setF(emptyInitiative('experiment')); setOpen(true); }}>+ experimento</Button>
            <Button size="sm" variant="primary" onClick={() => { setF(emptyInitiative('initiative')); setOpen(true); }}>+ iniciativa</Button>
          </div>
        )}
      </div>
      {canEdit && open && (
        <div className="rr-card rr-stack rr-stack--4">
          <MachineFill<{ title: string; kind: InitiativeKind; description: string; executor: Executor; ownerName: string | null; expectedEffect: string; measurementKind: MeasurementKind; measurementHow: string; measurementInWeeks: number; weakeningResult: string | null; estimatedHours: number | null }> form="initiative" projectId={projectId} placeholder="ex.: testar com 12 decoradoras se montam o pedido por paleta; a Regina executa; medir em 2 meses pelos pedidos" onFill={(r) => setF({ ...f, title: r.title || f.title, kind: r.kind ?? f.kind, description: r.description || f.description, executor: r.executor ?? f.executor, ownerName: r.ownerName ?? f.ownerName, expectedEffect: r.expectedEffect || f.expectedEffect, measurementKind: r.measurementKind ?? f.measurementKind, measurementHow: r.measurementHow || f.measurementHow, measurementDate: r.measurementInWeeks ? inWeeks(r.measurementInWeeks) : f.measurementDate, weakeningResult: r.weakeningResult ?? f.weakeningResult, estimatedHours: r.estimatedHours != null ? String(r.estimatedHours) : f.estimatedHours })} />
          <div className="rr-row rr-row--between">
            <Label tone="strong">{f.kind === 'experiment' ? 'novo experimento (PROVAR)' : 'nova iniciativa (FAZER)'}</Label>
            <button type="button" className="rr-label" onClick={() => setF({ ...f, kind: f.kind === 'experiment' ? 'initiative' : 'experiment' })}>{f.kind === 'experiment' ? 'é uma iniciativa, não um teste' : 'na verdade é um experimento'}</button>
          </div>
          <Field label="De onde nasce" htmlFor="i-o" hint="obrigatório: afirmação aceita da Solução, decisão ou hipótese. sem origem, é tarefa solta.">
            <Select id="i-o" value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })}>
              <option value="">— escolher —</option>
              {acceptedClaims.length > 0 && <optgroup label="afirmações da Solução">{acceptedClaims.map((c) => <option key={c.id} value={`claim:${c.id}`}>{c.text}</option>)}</optgroup>}
              {decisions.data && decisions.data.length > 0 && <optgroup label="decisões">{decisions.data.map((d) => <option key={d.id} value={`decision:${d.id}`}>{d.title}</option>)}</optgroup>}
              {hyps.data && hyps.data.length > 0 && <optgroup label="hipóteses">{hyps.data.map((h) => <option key={h.id} value={`hypothesis:${h.id}`}>{String(h.number).padStart(2, '0')} · {h.title}</option>)}</optgroup>}
            </Select>
          </Field>
          <Field label="O que vamos fazer" htmlFor="i-t"><Input id="i-t" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
          <Field label="Detalhe" htmlFor="i-d" hint="formato, canal, peça, mudança de processo, preço, treinamento… agnóstico à natureza."><Textarea id="i-d" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-start' }}>
            <Field label="Quem executa" htmlFor="i-x"><Select id="i-x" value={f.executor} onChange={(e) => setF({ ...f, executor: e.target.value as Executor })}>{EXECUTORS.map((x) => <option key={x} value={x}>{EXECUTOR_LABELS[x]}</option>)}</Select></Field>
            <Field label="Responsável" htmlFor="i-w"><Input id="i-w" value={f.ownerName} onChange={(e) => setF({ ...f, ownerName: e.target.value })} placeholder="nome" /></Field>
            <Field label="Horas estimadas" htmlFor="i-h" hint="opcional, para escopo"><Input id="i-h" type="number" value={f.estimatedHours} onChange={(e) => setF({ ...f, estimatedHours: e.target.value })} /></Field>
          </div>
          <div className="rr-measure-plan">
            <Label tone="strong">Aferição · obrigatória</Label>
            <Field label="Que efeito esperamos?" htmlFor="i-e"><Textarea id="i-e" rows={2} value={f.expectedEffect} onChange={(e) => setF({ ...f, expectedEffect: e.target.value })} placeholder="o que deve mudar se isso funcionar" /></Field>
            <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-start' }}>
              <Field label="Tipo" htmlFor="i-mk"><Select id="i-mk" value={f.measurementKind} onChange={(e) => setF({ ...f, measurementKind: e.target.value as MeasurementKind })}>{MEASUREMENT_KINDS.map((k) => <option key={k} value={k}>{MEASUREMENT_KIND_LABELS[k]}</option>)}</Select></Field>
              <Field label="Como medimos" htmlFor="i-mh"><Input id="i-mh" value={f.measurementHow} onChange={(e) => setF({ ...f, measurementHow: e.target.value })} placeholder="pedidos/semana, NPS, falas de lojistas…" /></Field>
              <Field label="Quando aferimos" htmlFor="i-md"><Input id="i-md" type="date" value={f.measurementDate} onChange={(e) => setF({ ...f, measurementDate: e.target.value })} /></Field>
            </div>
          </div>
          {f.kind === 'experiment' && (
            <Field label="Qual resultado enfraqueceria a hipótese?" htmlFor="i-wk" hint="obrigatório no experimento. sem critério de falseamento não há prova, só confirmação.">
              <Textarea id="i-wk" rows={2} value={f.weakeningResult} onChange={(e) => setF({ ...f, weakeningResult: e.target.value })} />
            </Field>
          )}
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" disabled={!ok || create.isPending} onClick={() => create.mutate()}>{f.kind === 'experiment' ? 'criar experimento' : 'criar iniciativa'}</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </div>
      )}
      {list.isSuccess && rows.length === 0 && <EmptyState title={filter === 'overdue' ? 'nenhuma aferição vencida.' : 'nenhuma iniciativa.'}>{filter === 'overdue' ? 'tudo que foi feito já foi medido, ou ainda não venceu.' : 'a Solução aprovada vira iniciativas. cada uma com efeito esperado e data de aferição.'}</EmptyState>}
      <div className="rr-stack rr-stack--4">{rows.map((i) => <InitiativeCard key={i.id} i={i} projectId={projectId} canEdit={canEdit} />)}</div>
    </section>
  );
}

export function InitiativeCard({ i, projectId, canEdit }: { i: Initiative; projectId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const [closing, setClosing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [result, setResult] = useState(i.result ?? '');
  const [outcome, setOutcome] = useState<ExperimentOutcome>('inconclusive');
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/initiatives/${i.id}`, body), onSuccess: () => { setClosing(false); invalidate(); } });
  const publish = useMutation({ mutationFn: () => api.post(`/projects/${projectId}/publications`, { section: 'make', entityType: 'initiative', entityId: i.id, status: 'published' }), onSuccess: invalidate });
  const active = i.status !== 'dropped' && i.status !== 'measured';
  return (
    <article className={cx('rr-exp', i.measurementOverdue && 'rr-exp--overdue')}>
      <div className="rr-exp__head">
        <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-start' }}>
          <button type="button" className="rr-imgpick__item" onClick={() => canEdit && setPicking(true)} title={canEdit ? 'foto da iniciativa' : undefined}><Thumb src={i.image?.thumbUrl ?? i.image?.url} size={64} /></button>
        <div className="rr-stack rr-stack--1">
          <div className="rr-row" style={{ gap: 'var(--rr-space-2)' }}>
            <Tag tone={i.kind === 'experiment' ? 'accent' : 'outline'}>{i.kind === 'experiment' ? 'experimento' : 'iniciativa'}</Tag>
            <span className="rr-exp__title">{i.title}</span>
          </div>
          <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)', gap: 'var(--rr-space-3)' }}>
            {i.claim && <span>implementa: «{i.claim.text}»</span>}
            {i.hypothesis && <Link to={`/hipoteses/${i.hypothesis.id}`} style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>{i.kind === 'experiment' ? 'testa' : 'implementa'}: {i.hypothesis.title}</Link>}
            <span>executa: {EXECUTOR_LABELS[i.executor]}{i.ownerName ? ` · ${i.ownerName}` : ''}</span>
            {i.estimatedHours != null && <span>{i.estimatedHours}h estimadas</span>}
            <button type="button" className="rr-budget__toggle" onClick={() => setBudgetOpen(!budgetOpen)}>{i.budget && i.budget.items > 0 ? `orçamento ${formatMoney(i.budget.estimated)}${i.budget.actual ? ` · realizado ${formatMoney(i.budget.actual)}` : ''}` : 'orçamento'} <Icon name={budgetOpen ? 'chevron-up' : 'chevron-down'} size={12} /></button>
            {i.published && !i.clientDecision && <span>na sala do cliente</span>}
            {i.clientDecision && <span style={{ color: i.clientDecision === 'approved' ? 'var(--rr-up)' : 'var(--rr-down)' }}>{CLIENT_DECISION_LABELS[i.clientDecision]}{i.clientDecidedAt ? ` · ${formatDate(i.clientDecidedAt)}` : ''}{i.clientNote ? ` · “${i.clientNote}”` : ''}</span>}
          </div>
        </div>
        </div>
        <div className="rr-row">
          <Tag tone={i.status === 'in_progress' ? 'accent' : 'outline'}>{INITIATIVE_STATUS_LABELS[i.status]}</Tag>
          {canEdit && i.status === 'planned' && <Button size="sm" variant="quiet" onClick={() => update.mutate({ status: 'in_progress', startsAt: new Date().toISOString() })}>{i.kind === 'experiment' ? 'ir a campo →' : 'começar →'}</Button>}
          {canEdit && i.status === 'in_progress' && i.kind === 'initiative' && <Button size="sm" variant="quiet" onClick={() => update.mutate({ status: 'done' })}>feita</Button>}
          {canEdit && i.kind === 'experiment' && !i.outcome && active && !closing && <Button size="sm" onClick={() => setClosing(true)}>registrar resultado</Button>}
          {canEdit && active && !measuring && <Button size="sm" variant={i.measurementOverdue ? 'primary' : 'quiet'} onClick={() => setMeasuring(true)}>medir efeito</Button>}
          {canEdit && active && !i.published && <Button size="sm" variant="quiet" onClick={() => publish.mutate()} disabled={publish.isPending} title="o cliente vê, aprova ou declina na sala">publicar na sala</Button>}
          {canEdit && active && <Button size="sm" variant="quiet" onClick={() => update.mutate({ status: 'dropped' })}>abandonar</Button>}
        </div>
      </div>
      {i.description && <div className="rr-exp__field rr-exp__field--wide"><span className="rr-label">O que</span><p>{i.description}</p></div>}
      <div className="rr-exp__field"><span className="rr-label">Efeito esperado</span><p>{i.expectedEffect}</p></div>
      <div className={cx('rr-exp__field', i.measurementOverdue && 'rr-exp__weaken')}>
        <span className="rr-label">{i.measurementOverdue ? 'Aferição vencida' : 'Aferição'}</span>
        <p>{MEASUREMENT_KIND_LABELS[i.measurementKind]} · {i.measurementHow} · {formatDate(i.measurementDate)}</p>
        <SeriesLink initiativeId={i.id} organizationId={i.organizationId} metricRef={i.metricRef} canEdit={canEdit && active} onChange={invalidate} />
      </div>
      {i.weakeningResult && <div className="rr-exp__field rr-exp__field--wide rr-exp__weaken"><span className="rr-label">Enfraqueceria a hipótese se</span><p>{i.weakeningResult}</p></div>}
      {(i.result || closing) && (
        <div className="rr-exp__result">
          <span className="rr-label">Resultado</span>
          {closing ? (
            <>
              <Textarea rows={3} value={result} onChange={(e) => setResult(e.target.value)} placeholder="o que observamos?" autoFocus />
              <div className="rr-row">
                <Select value={outcome} onChange={(e) => setOutcome(e.target.value as ExperimentOutcome)} style={{ width: 'auto' }}>
                  {EXPERIMENT_OUTCOMES.map((o) => <option key={o} value={o}>{EXPERIMENT_OUTCOME_LABELS[o]}</option>)}
                </Select>
                <Button variant="primary" size="sm" disabled={result.trim().length < 3 || update.isPending} onClick={() => update.mutate({ result, outcome, status: 'done' })}>concluir</Button>
                <Button variant="quiet" size="sm" onClick={() => setClosing(false)}>cancelar</Button>
              </div>
              {update.isError && <Notice tone="error">{(update.error as Error).message}</Notice>}
            </>
          ) : (
            <>
              <p style={{ color: 'var(--rr-text-2)' }}>{i.result}</p>
              {i.outcome && <span className={cx('rr-label', `rr-outcome--${i.outcome}`)}>{EXPERIMENT_OUTCOME_LABELS[i.outcome]} · {formatDate(i.completedAt)}</span>}
            </>
          )}
        </div>
      )}
      {budgetOpen && <div className="rr-exp__result"><span className="rr-label">Orçamento de produção</span><InitiativeBudget initiativeId={i.id} projectId={projectId} canEdit={canEdit} /></div>}
      <ImagePicker open={picking} onClose={() => setPicking(false)} organizationId={i.organizationId ?? ''} projectId={projectId} current={i.imageFileId} onPick={(fid) => update.mutate({ imageFileId: fid })} eyebrow="Foto da iniciativa" title={i.title} />
      {(i.effects.length > 0 || measuring) && (
        <div className="rr-exp__result">
          <span className="rr-label">Efeito medido</span>
          {i.effects.map((e) => <EffectRow key={e.id} e={e} />)}
          {measuring && <EffectForm projectId={projectId} initiative={i} onDone={() => { setMeasuring(false); invalidate(); }} onCancel={() => setMeasuring(false)} />}
        </div>
      )}
    </article>
  );
}

// ───────────────────────── DECISÕES (só sócio) ─────────────────────────

export function DecisionsSection({ projectId, canEdit, canDecide }: { projectId: string; canEdit: boolean; canDecide: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const list = useQuery({ queryKey: ['decisions', projectId], queryFn: () => api.get<Decision[]>(`/projects/${projectId}/decisions`) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: '', decision: '', rationale: '', decidedBy: '', alternatives: '' });
  const [basedOn, setBasedOn] = useState<EntitySummary[]>([]);
  const create = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/decisions`, {
        title: f.title,
        decision: f.decision,
        rationale: f.rationale || undefined,
        decidedBy: f.decidedBy.split(',').map((s) => s.trim()).filter(Boolean),
        alternatives: f.alternatives.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => { const [option, ...rest] = l.split(' — '); return { option: option!.trim(), why_not: rest.join(' — ').trim() || '—' }; }),
        evidenceIds: basedOn.filter((b) => b.type === 'evidence').map((b) => b.id),
        hypothesisIds: basedOn.filter((b) => b.type === 'hypothesis').map((b) => b.id),
      }),
    onSuccess: () => { setOpen(false); setF({ title: '', decision: '', rationale: '', decidedBy: '', alternatives: '' }); setBasedOn([]); invalidate(); },
  });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Decisões</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>o que, quando, quem, com quais evidências, quais alternativas, por quê, e o que aconteceu depois. só sócio registra.</span>
      </div>
      {canEdit && !canDecide && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>a Machine e a equipe preparam; quem bate o martelo é um sócio.</p>}
      {canDecide && (
        <Opener open={open} onOpen={() => setOpen(true)} label="+ registrar decisão">
          <Field label="Título" htmlFor="d-t"><Input id="d-t" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
          <Field label="O que foi decidido" htmlFor="d-d"><Textarea id="d-d" rows={3} value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} /></Field>
          <Field label="Por que decidimos" htmlFor="d-r"><Textarea id="d-r" rows={2} value={f.rationale} onChange={(e) => setF({ ...f, rationale: e.target.value })} /></Field>
          <Field label="Alternativas descartadas" htmlFor="d-a" hint="uma por linha: opção — por que não"><Textarea id="d-a" rows={2} value={f.alternatives} onChange={(e) => setF({ ...f, alternatives: e.target.value })} /></Field>
          <Field label="Outros que decidiram junto" htmlFor="d-w" hint="você já entra. nomes do cliente, separados por vírgula"><Input id="d-w" value={f.decidedBy} onChange={(e) => setF({ ...f, decidedBy: e.target.value })} /></Field>
          <Field label="Evidências e hipóteses que existiam" htmlFor="d-e" hint={basedOn.length ? basedOn.map((b) => b.title).join(' · ') : 'o contexto da decisão'}>
            <EntityPicker projectId={projectId} types={['evidence', 'hypothesis']} exclude={basedOn} onSelect={(e) => setBasedOn([...basedOn, e])} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" disabled={f.title.trim().length < 2 || f.decision.trim().length < 3 || create.isPending} onClick={() => create.mutate()}>registrar</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </Opener>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma decisão registrada.">decisão sem contexto vira opinião três meses depois.</EmptyState>}
      <div className="rr-stack rr-stack--3">{list.data?.map((d) => <DecisionCard key={d.id} d={d} projectId={projectId} canEdit={canDecide} />)}</div>
    </section>
  );
}

function DecisionCard({ d, projectId, canEdit }: { d: Decision; projectId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const [outcome, setOutcome] = useState('');
  const [editing, setEditing] = useState(false);
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/decisions/${d.id}`, body), onSuccess: () => { setEditing(false); invalidate(); } });
  const basedOnSummary = d.basedOn && d.basedOn.length > 0 ? Object.entries(d.basedOn.reduce<Record<string, number>>((a, b) => ({ ...a, [ENTITY_TYPE_LABELS[b.type]]: (a[ENTITY_TYPE_LABELS[b.type]] ?? 0) + 1 }), {})).map(([l, n]) => `${n} ${l}`).join(', ') : null;
  return (
    <article className="rr-decision">
      <div className="rr-row rr-row--between">
        <span className="rr-label rr-label--strong">{d.title}</span>
        <span className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
          <span>{formatDate(d.decidedAt)}</span>
          {d.decidedBy.length > 0 && <span>· {d.decidedBy.join(', ')}</span>}
          <Tag tone={d.status === 'done' ? 'accent' : 'outline'}>{DECISION_STATUS_LABELS[d.status]}</Tag>
        </span>
      </div>
      <p className="rr-decision__text">{d.decision}</p>
      <div className="rr-decision__grid">
        {d.rationale && <div><span className="rr-label">Por quê</span><p>{d.rationale}</p></div>}
        {basedOnSummary && <div><span className="rr-label">Baseada em</span><p>{basedOnSummary}</p></div>}
        {d.alternatives.length > 0 && <div><span className="rr-label">Alternativas</span>{d.alternatives.map((a, i) => <p key={i} className="rr-decision__alt"><b>{a.option}</b> — {a.why_not}</p>)}</div>}
      </div>
      {d.initiatives && d.initiatives.length > 0 && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>iniciativas: {d.initiatives.map((i) => `${i.title} (${INITIATIVE_STATUS_LABELS[i.status]})`).join(' · ')}</p>}
      {d.outcome ? (
        <div><span className="rr-label">Resultado posterior · {formatDate(d.outcomeAt)}</span><p style={{ color: 'var(--rr-text-2)' }}>{d.outcome}</p></div>
      ) : canEdit && (editing ? (
        <div className="rr-stack rr-stack--2">
          <Textarea rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="o que aconteceu depois?" autoFocus />
          <div className="rr-row">
            <Button size="sm" variant="primary" disabled={outcome.trim().length < 3} onClick={() => update.mutate({ outcome, status: 'done' })}>registrar resultado</Button>
            <Button size="sm" variant="quiet" onClick={() => setEditing(false)}>cancelar</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="quiet" onClick={() => setEditing(true)} style={{ alignSelf: 'flex-start' }}>registrar resultado posterior</Button>
      ))}
    </article>
  );
}

// ───────────────────────── EFEITOS (medir) ─────────────────────────

function EffectForm({ projectId, initiative, onDone, onCancel }: { projectId: string; initiative?: Initiative | null; onDone: () => void; onCancel: () => void }) {
  const initiatives = useQuery({ queryKey: ['initiatives', projectId], queryFn: () => api.get<Initiative[]>(`/projects/${projectId}/initiatives`), enabled: !initiative });
  const [fe, setFe] = useState({ text: '', kind: (initiative?.measurementKind ?? 'quantitative') as MeasurementKind, source: 'other' as EffectSource, metric: initiative?.measurementKind === 'quantitative' ? initiative.measurementHow : '', before: '', after: '', unit: '', direction: '0', initiativeId: initiative?.id ?? '' });
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/effects`, { text: fe.text, kind: fe.kind, source: fe.source, metric: fe.metric || undefined, before: fe.before ? Number(fe.before) : undefined, after: fe.after ? Number(fe.after) : undefined, unit: fe.unit || undefined, direction: Number(fe.direction), initiativeId: fe.initiativeId || undefined }),
    onSuccess: onDone,
  });
  return (
    <div className="rr-card rr-stack rr-stack--3">
      {initiative && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>esperávamos: {initiative.expectedEffect} · medir por {initiative.measurementHow}</p>}
      <Field label="O que mudou?" htmlFor="e-t"><Textarea id="e-t" rows={2} value={fe.text} onChange={(e) => setFe({ ...fe, text: e.target.value })} autoFocus /></Field>
      <div className="rr-row" style={{ gap: 'var(--rr-space-3)', alignItems: 'flex-end' }}>
        <Field label="Tipo" htmlFor="e-k"><Select id="e-k" value={fe.kind} onChange={(e) => setFe({ ...fe, kind: e.target.value as MeasurementKind })}>{MEASUREMENT_KINDS.map((k) => <option key={k} value={k}>{MEASUREMENT_KIND_LABELS[k]}</option>)}</Select></Field>
        <Field label="Métrica" htmlFor="e-m"><Input id="e-m" value={fe.metric} onChange={(e) => setFe({ ...fe, metric: e.target.value })} placeholder="acessos, pedidos, NPS" /></Field>
        <Field label="Antes" htmlFor="e-b"><Input id="e-b" type="number" value={fe.before} onChange={(e) => setFe({ ...fe, before: e.target.value })} /></Field>
        <Field label="Depois" htmlFor="e-a"><Input id="e-a" type="number" value={fe.after} onChange={(e) => setFe({ ...fe, after: e.target.value })} /></Field>
        <Field label="Unidade" htmlFor="e-u"><Input id="e-u" value={fe.unit} onChange={(e) => setFe({ ...fe, unit: e.target.value })} placeholder="%, R$, un" /></Field>
      </div>
      <div className="rr-row" style={{ gap: 'var(--rr-space-3)' }}>
        <Field label="Fonte" htmlFor="e-s"><Select id="e-s" value={fe.source} onChange={(e) => setFe({ ...fe, source: e.target.value as EffectSource })}>{EFFECT_SOURCES.map((s) => <option key={s} value={s}>{EFFECT_SOURCE_LABELS[s]}</option>)}</Select></Field>
        <Field label="Direção" htmlFor="e-d"><Select id="e-d" value={fe.direction} onChange={(e) => setFe({ ...fe, direction: e.target.value })}><option value="1">↑ melhorou</option><option value="0">↗ neutro</option><option value="-1">↓ piorou</option></Select></Field>
        {!initiative && (
          <Field label="Depois de" htmlFor="e-i"><Select id="e-i" value={fe.initiativeId} onChange={(e) => setFe({ ...fe, initiativeId: e.target.value })}><option value="">— iniciativa —</option>{initiatives.data?.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}</Select></Field>
        )}
      </div>
      {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
      <div className="rr-row"><Button size="sm" variant="primary" disabled={fe.text.trim().length < 3 || create.isPending} onClick={() => create.mutate()}>registrar efeito</Button><Button size="sm" variant="quiet" onClick={onCancel}>cancelar</Button></div>
    </div>
  );
}

export function EffectsSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const initiatives = useQuery({ queryKey: ['initiatives', projectId], queryFn: () => api.get<Initiative[]>(`/projects/${projectId}/initiatives`) });
  const effects = useQuery({ queryKey: ['effects', projectId], queryFn: () => api.get<Effect[]>(`/projects/${projectId}/effects`) });
  const [openE, setOpenE] = useState(false);
  const pending = initiatives.data?.filter((i) => i.status !== 'dropped' && i.effects.length === 0) ?? [];
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Efeito</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>o que mudou depois do que fizemos? o EFEITO fecha o ciclo: quantitativo, qualitativo ou sinal.</span>
      </div>
      {pending.length > 0 && (
        <div className="rr-stack rr-stack--2">
          <Label tone="strong">A aferir</Label>
          {pending.map((i) => (
            <div key={i.id} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}>
              <div>
                <div className="rr-list-row__title">{i.title}</div>
                <div className="rr-list-row__meta"><span>{INITIATIVE_STATUS_LABELS[i.status]}</span><span>esperamos: {i.expectedEffect}</span><span style={{ color: i.measurementOverdue ? 'var(--rr-down)' : undefined }}>{i.measurementOverdue ? 'vencida em' : 'aferir em'} {formatDate(i.measurementDate)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
      {effects.data && effects.data.length > 0 && <EffectCharts effects={effects.data} />}
      <div className="rr-stack rr-stack--3">
        <div className="rr-row rr-row--between">
          <Label tone="strong">Medido</Label>
          {canEdit && !openE && <Button size="sm" variant="quiet" onClick={() => setOpenE(true)}>+ efeito observado</Button>}
        </div>
        {openE && <EffectForm projectId={projectId} onDone={() => { setOpenE(false); invalidate(); }} onCancel={() => setOpenE(false)} />}
        {effects.isSuccess && effects.data.length === 0 && <EmptyState title="nenhum efeito observado.">vendas, CRM, analytics, social, pesquisas, NPS, campo, qualitativo.</EmptyState>}
        <div>{effects.data?.map((e) => <EffectRow key={e.id} e={e} />)}</div>
      </div>
    </section>
  );
}

/** Séries por métrica: cada efeito com métrica é um ponto (antes → depois). Com dois ou mais efeitos da mesma métrica, vira linha. */
export function effectSeries(effects: Effect[]) {
  const byMetric = new Map<string, { metric: string; unit: string | null; points: { at: Date; label: string; value: number }[] }>();
  const sorted = [...effects].filter((e) => e.metric && (e.after !== null || e.before !== null)).sort((a, b) => new Date(a.observedAt).getTime() - new Date(b.observedAt).getTime());
  for (const e of sorted) {
    const key = e.metric!.trim().toLowerCase();
    const s = byMetric.get(key) ?? { metric: e.metric!, unit: e.unit, points: [] };
    const at = new Date(e.observedAt);
    const label = at.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    if (s.points.length === 0 && e.before !== null) s.points.push({ at, label: 'antes', value: Number(e.before) });
    if (e.after !== null) s.points.push({ at, label, value: Number(e.after) });
    byMetric.set(key, s);
  }
  return [...byMetric.values()];
}

export function EffectCharts({ effects, compact }: { effects: Effect[]; compact?: boolean }) {
  const series = effectSeries(effects);
  // um número por métrica: do primeiro "antes" ao último "depois"
  const tiles = series
    .filter((sr) => sr.points.length >= 2 && sr.points[0]!.value !== 0)
    .slice(0, 4)
    .map((sr) => {
      const b = sr.points[0]!.value;
      const a = sr.points[sr.points.length - 1]!.value;
      const pct = Math.round(((a - b) / Math.abs(b)) * 100);
      return { value: `${pct > 0 ? '+' : ''}${pct}%`, label: `${sr.metric} · ${b} → ${a}${sr.unit ? ` ${sr.unit}` : ''}`, tone: (pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat') as 'up' | 'down' | 'flat' };
    });
  if (series.length === 0 && tiles.length === 0) return null;
  return (
    <div className={compact ? 'rr-stack rr-stack--4' : 'rr-results-split'}>
      <div className="rr-stack rr-stack--4">
        {series.map((sr) => {
          const first = sr.points[0]!.value;
          const last = sr.points[sr.points.length - 1]!.value;
          const delta = first !== 0 && sr.points.length > 1 ? `${last - first > 0 ? '+' : ''}${Math.round(((last - first) / Math.abs(first)) * 100)}%` : null;
          return (
            <div key={sr.metric} className="rr-chart-block">
              <div className="rr-chart-block__head"><Label>Evolução · {sr.metric}</Label><span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{sr.points.length} ponto(s){sr.unit ? ` · ${sr.unit}` : ''}</span></div>
              <LineChart points={sr.points.map((pt) => ({ label: pt.label, value: pt.value }))} unit={sr.unit ? ` ${sr.unit}` : ''} delta={delta} height={150} />
            </div>
          );
        })}
      </div>
      {tiles.length > 0 && <div className="rr-results-grid">{tiles.map((t, i) => <StatBig key={i} value={t.value} label={t.label} tone={t.tone} />)}</div>}
    </div>
  );
}

export function EffectRow({ e }: { e: Effect }) {
  const before = e.before !== null ? Number(e.before) : null;
  const after = e.after !== null ? Number(e.after) : null;
  const delta = before !== null && after !== null ? after - before : null;
  const pct = delta !== null && before ? Math.round((delta / Math.abs(before)) * 100) : null;
  const tone = e.direction > 0 ? 'rr-signal__icon--up' : e.direction < 0 ? 'rr-signal__icon--down' : 'rr-signal__icon--flat';
  return (
    <div className="rr-effect">
      <div className={cx('rr-effect__delta', tone)}>
        {delta !== null ? `${delta > 0 ? '+' : ''}${pct !== null ? `${pct}%` : delta}` : e.direction > 0 ? '↑' : e.direction < 0 ? '↓' : '↗'}
        {e.metric && <small>{e.metric}{before !== null && after !== null ? ` · ${before} → ${after}${e.unit ? ` ${e.unit}` : ''}` : ''}</small>}
      </div>
      <div>
        <div>{e.text}</div>
        <div className="rr-effect__meta"><span>{MEASUREMENT_KIND_LABELS[e.kind]}</span><span>{EFFECT_SOURCE_LABELS[e.source]}</span><span>{formatDate(e.observedAt)}</span>{e.initiative && <span>depois de: {e.initiative.title}</span>}</div>
      </div>
    </div>
  );
}

// ───────────────────────── APRENDIZADOS ─────────────────────────

export function LearningsSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const list = useQuery({ queryKey: ['learnings', projectId], queryFn: () => api.get<Learning[]>(`/projects/${projectId}/learnings`) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ text: '', context: '', scope: 'project' as LearningScope, anonymizedText: '' });
  const [derived, setDerived] = useState<EntitySummary[]>([]);
  const create = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/learnings`, { text: f.text, context: f.context || undefined, scope: f.scope, anonymizedText: f.anonymizedText || undefined, derivedFrom: derived.map((d) => ({ type: d.type, id: d.id })) }),
    onSuccess: () => { setOpen(false); setF({ text: '', context: '', scope: 'project', anonymizedText: '' }); setDerived([]); invalidate(); },
  });
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Aprendizados</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>conhecimento consolidado e reutilizável. o ativo da Raro.</span>
      </div>
      {canEdit && (
        <Opener open={open} onOpen={() => setOpen(true)} label="+ novo aprendizado">
          <Field label="O que aprendemos" htmlFor="l-t"><Textarea id="l-t" rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} autoFocus /></Field>
          <Field label="Contexto" htmlFor="l-c" hint="em que situação isso valeu?"><Input id="l-c" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} /></Field>
          <Field label="Alcance" htmlFor="l-s" hint="regra 17.8: conhecimento cruzado só sai do projeto abstraído e autorizado">
            <Select id="l-s" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value as LearningScope })}>{LEARNING_SCOPES.map((s) => <option key={s} value={s}>{LEARNING_SCOPE_LABELS[s]}</option>)}</Select>
          </Field>
          {f.scope === 'anonymized' && <Field label="Versão abstraída" htmlFor="l-a" hint="sem nomes, marcas, números ou dados do cliente"><Textarea id="l-a" rows={2} value={f.anonymizedText} onChange={(e) => setF({ ...f, anonymizedText: e.target.value })} /></Field>}
          <Field label="Deriva de" htmlFor="l-d" hint={derived.length ? derived.map((d) => d.title).join(' · ') : 'iniciativa, decisão, efeito, evidência…'}>
            <EntityPicker projectId={projectId} types={['initiative', 'decision', 'effect', 'evidence', 'signal', 'hypothesis', 'claim']} exclude={derived} onSelect={(e) => setDerived([...derived, e])} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" disabled={f.text.trim().length < 3 || create.isPending} onClick={() => create.mutate()}>registrar</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </Opener>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nada consolidado ainda.">nada importante deve morrer numa apresentação, num Figma ou na cabeça de alguém.</EmptyState>}
      <div>{list.data?.map((l) => <LearningRow key={l.id} l={l} />)}</div>
    </section>
  );
}

export function LearningRow({ l, showProject }: { l: Learning; showProject?: boolean }) {
  return (
    <div className="rr-learning">
      <p className="rr-learning__text">{l.scope === 'anonymized' && showProject ? l.anonymizedText ?? l.text : l.text}</p>
      <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
        <Tag tone={l.scope === 'project' ? 'outline' : 'accent'}>{LEARNING_SCOPE_LABELS[l.scope]}</Tag>
        {showProject && l.organization && l.scope !== 'anonymized' && <span>{l.organization.name} · {l.project?.name}</span>}
        {l.context && <span>{l.context}</span>}
        {l.derivedFrom && l.derivedFrom.length > 0 && <span>deriva de {l.derivedFrom.map((d) => ENTITY_TYPE_LABELS[d.type]).join(', ')}</span>}
        <span>{formatDate(l.createdAt)}</span>
      </div>
    </div>
  );
}
